'use client';

import { useEffect, useRef, useState } from 'react';

import {
  ConversationProvider,
  useConversationControls,
  useConversationMode,
  useConversationStatus,
} from '@elevenlabs/react';

import { trackVoiceDemoEngaged } from '~/lib/analytics/posthog-events';

/**
 * Our own inline voice UI for the Selenos interviewer — built on the
 * ElevenLabs React SDK instead of the drop-in `<elevenlabs-convai>` widget
 * (which only renders as a floating corner overlay). This component lives in
 * the page flow and is fully Selenos-branded.
 *
 * The SDK owns the conversation engine (WebRTC mic in, agent audio out,
 * connection + speaking/listening state, transcript). We own every pixel.
 */

type TranscriptTurn = { role: 'user' | 'ai'; text: string };

export function VoiceInterviewInline({ agentId }: { agentId: string }) {
  const [turns, setTurns] = useState<TranscriptTurn[]>([]);

  return (
    <ConversationProvider
      agentId={agentId}
      connectionType="webrtc"
      onConnect={({ conversationId }) => {
        // Voice cohort engagement signal (experiment H7). `conversationId` is
        // the lead's `web_session_id` merge key, so this joins back to the
        // captured lead for voice-vs-text conversion comparison.
        trackVoiceDemoEngaged(conversationId);
      }}
      onMessage={({ message, source }) =>
        setTurns((prev) => [...prev, { role: source, text: message }])
      }
    >
      <VoiceOrb hasTranscript={turns.length > 0} onReset={() => setTurns([])} />
      <LiveTranscript turns={turns} />
    </ConversationProvider>
  );
}

// Languages the agent has a voice preset for (de=Monika, fr=Elia, en=Sarah).
// Detect from the browser locale; fall back to English for anything else
// (passing an un-presetted language would use the base voice on a wrong accent).
const PRESET_LANGUAGES = ['de', 'fr', 'en'] as const;
type PresetLanguage = (typeof PRESET_LANGUAGES)[number];

function detectLanguage(): PresetLanguage {
  if (typeof navigator === 'undefined') return 'en';
  const candidates = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  for (const tag of candidates) {
    const base = tag?.split('-')[0]?.toLowerCase();
    if (base && (PRESET_LANGUAGES as readonly string[]).includes(base)) {
      return base as PresetLanguage;
    }
  }
  return 'en';
}

function VoiceOrb({
  hasTranscript,
  onReset,
}: {
  hasTranscript: boolean;
  onReset: () => void;
}) {
  const { status } = useConversationStatus();
  const { isSpeaking } = useConversationMode();
  const { startSession, endSession } = useConversationControls();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const active = status === 'connected';
  const connecting = status === 'connecting' || starting;

  async function handleStart() {
    setError(null);
    setStarting(true);
    try {
      // Explicit mic-permission step = the affirmative consent action before
      // any audio is captured (the page already shows the AI-disclosure + recording notice).
      await navigator.mediaDevices.getUserMedia({ audio: true });
      if (hasTranscript) onReset(); // fresh transcript per conversation
      // Match the user's detected locale: greet + speak in their language with
      // the matching voice (agent has per-language presets for de/fr/en).
      startSession({ overrides: { agent: { language: detectLanguage() } } });
    } catch {
      setError(
        'Microphone access is needed to talk. Please allow it and try again.',
      );
    } finally {
      setStarting(false);
    }
  }

  const label = !active
    ? connecting
      ? 'Connecting…'
      : 'Tap to start'
    : isSpeaking
      ? 'Speaking'
      : 'Listening';

  return (
    <div className="flex flex-col items-center gap-7">
      <button
        type="button"
        onClick={active ? () => endSession() : handleStart}
        disabled={connecting}
        aria-label={active ? 'End conversation' : 'Start voice conversation'}
        className="group relative grid size-48 place-items-center rounded-full transition-transform outline-none disabled:cursor-wait"
      >
        {/* Expanding ring while connected */}
        {active && (
          <span className="absolute inset-0 animate-ping rounded-full bg-sky-400/30 [animation-duration:2s]" />
        )}
        {/* The orb */}
        <span
          className={[
            'absolute inset-2 rounded-full shadow-xl transition-all duration-500',
            'bg-[radial-gradient(circle_at_30%_30%,#7dd3fc_0%,#38bdf8_35%,#2563eb_70%,#1e3a8a_100%)]',
            connecting ? 'animate-spin [animation-duration:3s]' : '',
            isSpeaking
              ? 'scale-105 brightness-110 saturate-150'
              : active
                ? 'animate-pulse [animation-duration:2.5s]'
                : 'opacity-90 group-hover:scale-105 group-hover:opacity-100',
          ].join(' ')}
        />
        {/* Inner glass highlight */}
        <span className="absolute inset-2 rounded-full bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.55),transparent_45%)]" />
      </button>

      <div className="bg-muted text-foreground/70 rounded-full px-4 py-1.5 text-sm font-medium">
        {label}
      </div>

      {active && (
        <button
          type="button"
          onClick={() => endSession()}
          className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4 transition-colors"
        >
          End conversation
        </button>
      )}

      {error && <p className="max-w-xs text-sm text-red-500">{error}</p>}
    </div>
  );
}

/** Live, scrolling transcript of the conversation. */
function LiveTranscript({ turns }: { turns: TranscriptTurn[] }) {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns]);

  if (turns.length === 0) return null;

  return (
    <div className="bg-background/50 mt-8 max-h-72 w-full max-w-md space-y-3 overflow-y-auto rounded-xl border p-4 text-left">
      {turns.map((turn, i) => (
        <div
          key={i}
          className={
            turn.role === 'user' ? 'flex justify-end' : 'flex justify-start'
          }
        >
          <span
            className={[
              'inline-block max-w-[85%] rounded-2xl px-3.5 py-2 text-sm',
              turn.role === 'user'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground',
            ].join(' ')}
          >
            {turn.text}
          </span>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
